import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

/** Bold, code, internal links. */
function inline(text: string, onNavigate: () => void): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;
  let last = 0;
  let key = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text))) {
    if (match.index > last) out.push(text.slice(last, match.index));
    const token = match[0];
    if (token.startsWith('**')) out.push(<b key={key++}>{token.slice(2, -2)}</b>);
    else if (token.startsWith('`')) out.push(<code key={key++}>{token.slice(1, -1)}</code>);
    else {
      const [, label, href] = token.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/) ?? [];
      // internal links only
      out.push(
        href?.startsWith('/') && !href.startsWith('//') ? (
          <Link key={key++} to={href} onClick={onNavigate}>
            {label}
          </Link>
        ) : (
          <Fragment key={key++}>{label}</Fragment>
        ),
      );
    }
    last = match.index + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function blocks(text: string, onNavigate: () => void, offset: number): ReactNode[] {
  return text
    .split(/\n{2,}/)
    .map((block) => block.split('\n').filter((line) => line.trim()))
    .filter((lines) => lines.length > 0)
    .map((lines, i) => {
      const key = `${offset}-${i}`;
      if (lines.every((line) => /^\s*([-*]|\d+\.)\s+/.test(line))) {
        const items = lines.map((line, j) => <li key={j}>{inline(line.replace(/^\s*([-*]|\d+\.)\s+/, ''), onNavigate)}</li>);
        return /^\s*\d+\./.test(lines[0]) ? <ol key={key}>{items}</ol> : <ul key={key}>{items}</ul>;
      }
      return <p key={key}>{inline(lines.join(' ').replace(/^#{1,6}\s+/, ''), onNavigate)}</p>;
    });
}

export function MarkdownLite({ text, onNavigate }: { text: string; onNavigate: () => void }) {
  const parts = text.replace(/\r/g, '').split(/(```[\s\S]*?```)/g);
  return (
    <>
      {parts.flatMap((part, i) =>
        part.startsWith('```') ? (
          <pre key={`code-${i}`}>
            <code>{part.replace(/^```[\w-]*\n?/, '').replace(/```$/, '')}</code>
          </pre>
        ) : (
          blocks(part, onNavigate, i)
        ),
      )}
    </>
  );
}
